/**
 * The live WebSocket transport.
 *
 * Responsibilities, and nothing else:
 *   - connection lifecycle with exponential backoff (spec 8.6 — drops on a
 *     mobile network are normal, not exceptional)
 *   - the clock-sync handshake (spec 8.1)
 *   - stamping every outbound action with `clientTs` so the server can apply
 *     its latency-fair clamp (spec 8.3)
 *
 * It does NOT decrypt round payloads, schedule reveals, or interpret game
 * state. Those belong to the store, so that the local driver gets the identical
 * treatment and there is exactly one reveal path.
 */

import {
  ASSET_TOKEN_RE,
  CLOCK_SYNC_INTERVAL_MS,
  PROTOCOL_VERSION,
  encodeMessage,
  parseServerMessage,
} from '@geogame/shared';
import type { ClientMessage, ServerMessage } from '@geogame/shared';

import { ClockSync } from '../lib/clock';
import { Emitter } from './transport';
import type { ConnectionStatus, GameTransport, TransportStats } from './transport';

const BACKOFF_BASE_MS = 500;
const BACKOFF_FACTOR = 1.8;
const BACKOFF_MAX_MS = 10_000;
/** Rapid pings right after connect, to fill the sample window before round 1. */
const BURST_PINGS = 4;
const BURST_INTERVAL_MS = 220;

export interface SocketTransportOptions {
  readonly url?: string;
  readonly handle?: string;
  /**
   * What to send on `hello`: the match token if a match is live, otherwise the
   * identity token. The gateway accepts either (`onHello` tries `resume` then
   * falls back to `id`), which is what lets one field carry both.
   */
  readonly getResumeToken: () => string | null;
  /**
   * TWO TOKENS, TWO SETTERS, AND THEY MUST NOT SHARE A SLOT.
   *
   * `welcome` carries an IDENTITY token: kind `id`, thirty days, the only
   * thing that proves this browser is a particular player. `matched` carries a
   * MATCH token: kind `resume`, ONE HOUR, naming the match to rejoin.
   *
   * They used to be written to the same key by a single `setResumeToken`, so
   * playing a match replaced a thirty-day identity with a one-hour one. The
   * visible half was that REST sign-in started returning `bad_device`, since
   * `deviceOf` verifies kind `id` and would not take a `resume`. The quiet
   * half was worse: an hour later the stored token was expired, `hello` could
   * verify it as neither kind, and the gateway issued a NEW player id -- so a
   * browser's coverage, rivalries and rating were silently orphaned roughly an
   * hour after every match it played.
   */
  readonly setIdentityToken: (token: string) => void;
  readonly setMatchToken: (token: string) => void;
}

/**
 * Where `/api` lives. Same origin unless the build was pointed elsewhere —
 * and there is no third possibility, because after the flag vendoring this
 * codebase contacts exactly one host: the one that armed the round.
 */
function apiBase(): string {
  const configured = import.meta.env['VITE_API_URL'];
  return typeof configured === 'string' && configured.length > 0 ? configured : '';
}

function defaultUrl(): string {
  const configured = import.meta.env['VITE_WS_URL'];
  if (typeof configured === 'string' && configured.length > 0) return configured;
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws`;
}

export class SocketTransport implements GameTransport {
  private readonly url: string;
  private readonly opts: SocketTransportOptions;
  private readonly clock = new ClockSync();
  private readonly messages = new Emitter<ServerMessage>();
  private readonly statuses = new Emitter<ConnectionStatus>();

  private ws: WebSocket | null = null;
  private status: ConnectionStatus = 'idle';
  private attempt = 0;
  private closedByUs = false;
  private pingTimer: number | null = null;
  private burstTimer: number | null = null;
  private reconnectTimer: number | null = null;
  /** Queued only while the socket is down — never gameplay input (see store). */
  private outbox: ClientMessage[] = [];

  constructor(opts: SocketTransportOptions) {
    this.opts = opts;
    this.url = opts.url ?? defaultUrl();
  }

  onMessage(fn: (msg: ServerMessage) => void): () => void {
    return this.messages.on(fn);
  }

  onStatus(fn: (status: ConnectionStatus) => void): () => void {
    fn(this.status);
    return this.statuses.on(fn);
  }

  serverNow(): number {
    return this.clock.serverNow();
  }

  stats(): TransportStats {
    const r = this.clock.read();
    return { rttMs: r.rttMs, offsetMs: r.offsetMs, synced: r.synced };
  }

  /**
   * One GET against our own gateway, for bytes that are sealed before they
   * leave it. The token comes off `round.arm`, so it is validated against the
   * same shape the protocol parser enforces before it is ever concatenated
   * into a path — a token is untrusted input like every other wire field.
   */
  async fetchAsset(token: string): Promise<Uint8Array> {
    if (!ASSET_TOKEN_RE.test(token)) throw new Error('malformed asset token');
    const res = await fetch(`${apiBase()}/api/asset/${token}`, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) throw new Error(`asset fetch failed (${res.status})`);
    return new Uint8Array(await res.arrayBuffer());
  }

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.closedByUs = false;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      const hello: ClientMessage = {
        t: 'hello',
        v: PROTOCOL_VERSION,
        ...(this.opts.getResumeToken() ? { resumeToken: this.opts.getResumeToken() as string } : {}),
        ...(this.opts.handle ? { handle: this.opts.handle } : {}),
      };
      this.raw(hello);
      this.startPinging();
      const pending = this.outbox;
      this.outbox = [];
      for (const msg of pending) this.raw(msg);
      // Status is announced LAST, and deliberately so. `statuses.emit` runs its
      // subscribers synchronously, and the store reacts to 'open' by sending
      // `queue` — which would then leave on the wire ahead of our own `hello`
      // and be rejected with `unauthorized: send hello first`. The gateway is
      // right to reject it; the ordering here is what has to be correct.
      this.setStatus('open');
    };

    ws.onmessage = (ev: MessageEvent<unknown>) => {
      if (typeof ev.data !== 'string') return;
      const msg = parseServerMessage(ev.data);
      // A malformed frame is dropped, never partially trusted.
      if (!msg) return;
      this.handle(msg);
      this.messages.emit(msg);
    };

    ws.onerror = () => {
      // `onclose` always follows; the reconnect decision lives there.
    };

    ws.onclose = () => {
      this.stopPinging();
      this.ws = null;
      if (this.closedByUs) {
        this.setStatus('closed');
        return;
      }
      this.scheduleReconnect();
    };
  }

  close(): void {
    this.closedByUs = true;
    this.stopPinging();
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.outbox = [];
    this.ws?.close();
    this.ws = null;
    this.setStatus('closed');
  }

  /**
   * Send, queueing only if the socket is momentarily down. Gameplay actions are
   * never routed here while disconnected — the store discards them, because a
   * bank that lands four seconds late is worse than one that never landed.
   */
  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.raw(msg);
      return;
    }
    if (msg.t === 'hello' || msg.t === 'queue' || msg.t === 'dequeue') {
      this.outbox.push(msg);
    }
  }

  private raw(msg: ClientMessage): void {
    try {
      this.ws?.send(encodeMessage(msg));
    } catch {
      // A send on a socket that died between the readyState check and here is
      // exactly the case `onclose` is about to handle.
    }
  }

  private handle(msg: ServerMessage): void {
    switch (msg.t) {
      case 'pong':
        this.clock.addSample(msg.t0, msg.t1);
        break;
      case 'welcome':
        this.clock.seed(msg.serverTs);
        // Kind `id`, thirty days. See `setIdentityToken`.
        this.opts.setIdentityToken(msg.resumeToken);
        break;
      case 'matched':
        this.clock.seed(msg.serverTs);
        // Kind `resume`, one hour, and NOT an identity. See `setMatchToken`.
        this.opts.setMatchToken(msg.resumeToken);
        break;
      default:
        break;
    }
  }

  private startPinging(): void {
    this.stopPinging();
    let burst = 0;
    const ping = (): void => this.raw({ t: 'ping', t0: Date.now() });
    ping();
    this.burstTimer = window.setInterval(() => {
      burst += 1;
      if (burst >= BURST_PINGS) {
        if (this.burstTimer !== null) window.clearInterval(this.burstTimer);
        this.burstTimer = null;
        return;
      }
      ping();
    }, BURST_INTERVAL_MS);
    this.pingTimer = window.setInterval(ping, CLOCK_SYNC_INTERVAL_MS);
  }

  private stopPinging(): void {
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    if (this.burstTimer !== null) window.clearInterval(this.burstTimer);
    this.pingTimer = null;
    this.burstTimer = null;
  }

  private scheduleReconnect(): void {
    this.setStatus('reconnecting');
    const raw = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * BACKOFF_FACTOR ** this.attempt);
    // Full jitter in the upper half of the window: spreads a fleet-wide
    // reconnect without ever waiting a pathologically short time.
    const delay = raw * (0.5 + Math.random() * 0.5);
    this.attempt += 1;
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private setStatus(next: ConnectionStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.statuses.emit(next);
  }
}
